import { Routes, Route } from '@angular/router';
import { roleGuard } from 'src/lib/guards/admin-role.guard';
import { permissionGuard } from '../../../lib/guards/permission.guard';
import { featureGuard } from '../../../lib/guards/brand-feature.guard';
import { BOARD_CONFIGS } from './base/board-configs';
import { BoardConfig } from './base/board.types';

/**
 * BBS 게시판 타입 정의
 */
type BbsBoardKey =
  | 'notice'
  | 'event'
  | 'faq'
  | 'column'
  | 'review'
  | 'cms'
  | 'hmpg'
  | 'popup';

/**
 * Board Route Factory Function (rxResource 전환)
 *
 * Angular 21+ Modern Pattern:
 * - ❌ Resolver 제거 (느린 라우트 전환 해결)
 * - ✅ Component-level rxResource (즉시 라우트 전환)
 * - ✅ 자동 로딩/에러 상태 관리
 *
 * AS-IS (Resolver):
 * - 라우트 전환 전 데이터 로드 대기 → 느린 UX
 *
 * TO-BE (rxResource):
 * - 즉시 라우트 전환 → 빠른 UX
 * - 로딩 스피너 표시 가능
 *
 * 새로운 게시판 추가 시:
 * 1. BOARD_CONFIGS에 config 추가
 * 2. BOARD_KEYS 배열에 key 추가
 *
 * @param boardKey - 게시판 키
 * @param config - 게시판 설정
 * @returns Route 객체
 */
function createBoardRoute(boardKey: BbsBoardKey, config: BoardConfig): Route {
  return {
    path: config.routePath,
    canActivate: [
      ...(config.permissions?.map((p) => permissionGuard(p as any)) || []),
      ...(config.features?.map((f) => featureGuard(f as any)) || []),
      ...(config.roles?.map((r) => roleGuard(r)) || []),
    ],
    children: [
      {
        path: '',
        redirectTo: 'list',
        pathMatch: 'full',
      },
      {
        path: 'list',
        loadComponent: () =>
          import(
            './components/smart/board-list-page/board-list-page.component'
          ).then((m) => m.BoardListPageComponent),
        // ❌ Resolver 제거: runGuardsAndResolvers, resolve 삭제
        // ✅ rxResource가 자동으로 query params 변경 감지
        data: {
          boardKey,
          config,
          title: getTitleByBoardKey(boardKey),
        },
        children: [
          {
            path: ':id',
            loadComponent: () =>
              import(
                './components/smart/board-detail-page/board-detail-page.component'
              ).then((m) => m.BoardDetailPageComponent),
            // ❌ Resolver 제거: runGuardsAndResolvers, resolve 삭제
            // ✅ rxResource가 자동으로 params 변경 감지
            data: {
              boardKey,
              config,
              title: `${getTitleByBoardKey(boardKey)} 상세`,
            },
          },
        ],
      },
    ],
  };
}

/**
 * 게시판 키로 타이틀 반환
 */
function getTitleByBoardKey(boardKey: BbsBoardKey): string {
  const titles: Record<BbsBoardKey, string> = {
    notice: '공지사항',
    event: '이벤트',
    faq: 'FAQ',
    column: '칼럼',
    review: '리뷰',
    cms: 'CMS',
    hmpg: '홈페이지',
    popup: '팝업',
  };
  return titles[boardKey];
}

/**
 * 통합 게시판 라우팅 (Angular 21+ rxResource)
 *
 * **AS-IS (Legacy + Resolver)**:
 * - 5개 별도 컴포넌트 (BbsNoticeComponent, BbsEventComponent, ...)
 * - 5개 별도 라우팅 (253줄)
 * - Resolver 사용 → 느린 라우트 전환
 *
 * **TO-BE (Modern + rxResource)**:
 * - 1개 Smart 컴포넌트 (BoardListPageComponent)
 * - Config-Driven 동적 라우팅 (~80줄)
 * - rxResource 사용 → 즉시 라우트 전환
 *
 * **장점**:
 * - 253줄 → 80줄 (68% 감소)
 * - Resolver 제거 → UX 개선
 * - 자동 로딩/에러 상태 관리
 * - 새 게시판 추가 시 2줄만 수정
 * - Type-safe 라우팅
 */
export const boardRoutes: Routes = (
  [
    'notice',
    'event',
    'faq',
    'column',
    'review',
    'cms',
    'hmpg',
    'popup',
  ] as BbsBoardKey[]
).map((key) => createBoardRoute(key, BOARD_CONFIGS[key]));
